import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Users, 
    Search, 
    Plus, 
    Upload, 
    Tag, 
    Trash2, 
    Edit2,
    Mail,
    X,
    Check,
    AlertCircle,
    FileText,
    UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactsPage = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTag, setSelectedTag] = useState('');
    const [tags, setTags] = useState([]);
    const [stats, setStats] = useState(null);
    const [selectedContacts, setSelectedContacts] = useState([]);
    
    // Modals
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    
    // Form states
    const [newContact, setNewContact] = useState({ email: '', name: '', tags: [], notes: '' });
    const [importData, setImportData] = useState({ emailText: '', tag: '' });
    const [newTagInput, setNewTagInput] = useState('');

    useEffect(() => {
        fetchContacts();
        fetchTags();
        fetchStats();
    }, [selectedTag, searchQuery]);

    const fetchContacts = async () => {
        try {
            const params = new URLSearchParams();
            if (selectedTag) params.append('tag', selectedTag);
            if (searchQuery) params.append('search', searchQuery);
            
            const response = await axios.get(`${API}/admin/contacts?${params.toString()}`);
            setContacts(response.data);
        } catch (error) {
            toast.error('Failed to load contacts');
        } finally {
            setLoading(false);
        }
    };

    const fetchTags = async () => {
        try {
            const response = await axios.get(`${API}/admin/contacts/tags`);
            setTags(response.data);
        } catch (error) {
            console.error('Failed to fetch tags');
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/admin/contacts/stats`);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats');
        }
    };

    const handleAddContact = async () => {
        if (!newContact.email) {
            toast.error('Email is required');
            return;
        }
        
        try {
            await axios.post(`${API}/admin/contacts`, newContact);
            toast.success('Contact added successfully');
            setAddModalOpen(false);
            setNewContact({ email: '', name: '', tags: [], notes: '' });
            fetchContacts();
            fetchTags();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add contact');
        }
    };

    const handleImport = async () => {
        const emails = importData.emailText
            .split(/[\n,;]/)
            .map(e => e.trim())
            .filter(e => e && e.includes('@'));
        
        if (emails.length === 0) {
            toast.error('No valid emails found');
            return;
        }

        try {
            const response = await axios.post(`${API}/admin/contacts/import`, {
                emails,
                tag: importData.tag || null
            });
            toast.success(response.data.message);
            setImportModalOpen(false);
            setImportData({ emailText: '', tag: '' });
            fetchContacts();
            fetchTags();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Import failed');
        }
    };

    const handleUpdateContact = async () => {
        if (!editingContact) return;
        
        try {
            await axios.put(`${API}/admin/contacts/${editingContact.id}`, {
                name: editingContact.name,
                tags: editingContact.tags,
                notes: editingContact.notes
            });
            toast.success('Contact updated');
            setEditModalOpen(false);
            setEditingContact(null);
            fetchContacts();
            fetchTags();
        } catch (error) {
            toast.error('Failed to update contact');
        }
    };

    const handleDeleteContact = async (contactId) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;
        
        try {
            await axios.delete(`${API}/admin/contacts/${contactId}`);
            toast.success('Contact deleted');
            fetchContacts();
            fetchStats();
        } catch (error) {
            toast.error('Failed to delete contact');
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedContacts.length === 0) return;
        if (!window.confirm(`Delete ${selectedContacts.length} contacts?`)) return;
        
        try {
            await axios.delete(`${API}/admin/contacts/bulk`, { data: selectedContacts });
            toast.success(`Deleted ${selectedContacts.length} contacts`);
            setSelectedContacts([]);
            fetchContacts();
            fetchStats();
        } catch (error) {
            toast.error('Failed to delete contacts');
        }
    };

    const toggleSelectAll = () => {
        if (selectedContacts.length === contacts.length) {
            setSelectedContacts([]);
        } else {
            setSelectedContacts(contacts.map(c => c.id));
        }
    };

    const toggleSelectContact = (contactId) => {
        setSelectedContacts(prev => 
            prev.includes(contactId) 
                ? prev.filter(id => id !== contactId)
                : [...prev, contactId]
        );
    };

    const addTagToForm = (formSetter, currentTags) => {
        if (newTagInput.trim() && !currentTags.includes(newTagInput.trim())) {
            formSetter(prev => ({ ...prev, tags: [...prev.tags, newTagInput.trim()] }));
            setNewTagInput('');
        }
    };

    const removeTagFromForm = (formSetter, tag) => {
        formSetter(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setImportData(prev => ({ ...prev, emailText: event.target.result }));
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[#3D4435]">Email Contacts</h1>
                    <p className="text-gray-600">Manage your mailing list for campaigns and newsletters</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => setImportModalOpen(true)}
                        variant="outline"
                        className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        Import
                    </Button>
                    <Button
                        onClick={() => setAddModalOpen(true)}
                        className="bg-[#3D4435] hover:bg-[#4A5240] text-white"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Contact
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-[#C9A227]/20 rounded-lg">
                                    <Users className="w-5 h-5 text-[#C9A227]" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-[#3D4435]">{stats.total_contacts}</p>
                                    <p className="text-sm text-gray-500">External Contacts</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-green-100 rounded-lg">
                                    <UserPlus className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-[#3D4435]">{stats.registered_members}</p>
                                    <p className="text-sm text-gray-500">Registered Members</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-blue-100 rounded-lg">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-[#3D4435]">{stats.total_reachable}</p>
                                    <p className="text-sm text-gray-500">Total Reachable</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Search by email or name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        
                        {/* Tag Filter */}
                        <div className="flex gap-2 flex-wrap">
                            <Button
                                variant={selectedTag === '' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setSelectedTag('')}
                                className={selectedTag === '' ? 'bg-[#3D4435]' : ''}
                            >
                                All
                            </Button>
                            {tags.map((t) => (
                                <Button
                                    key={t.tag}
                                    variant={selectedTag === t.tag ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedTag(t.tag)}
                                    className={selectedTag === t.tag ? 'bg-[#C9A227]' : ''}
                                >
                                    <Tag className="w-3 h-3 mr-1" />
                                    {t.tag} ({t.count})
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Bulk Actions */}
            {selectedContacts.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-amber-800">
                        {selectedContacts.length} contact(s) selected
                    </span>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteSelected}
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Selected
                    </Button>
                </div>
            )}

            {/* Contacts Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading contacts...</div>
                    ) : contacts.length === 0 ? (
                        <div className="p-8 text-center">
                            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-600">No contacts yet</h3>
                            <p className="text-gray-500 mb-4">Import emails or add contacts manually</p>
                            <Button onClick={() => setImportModalOpen(true)} className="bg-[#C9A227] hover:bg-[#B8922A]">
                                <Upload className="w-4 h-4 mr-2" />
                                Import Contacts
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="w-10 px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedContacts.length === contacts.length && contacts.length > 0}
                                                onChange={toggleSelectAll}
                                                className="w-4 h-4 rounded"
                                            />
                                        </th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Email</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Name</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Tags</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Source</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Added</th>
                                        <th className="w-24 px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contacts.map((contact) => (
                                        <tr key={contact.id} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedContacts.includes(contact.id)}
                                                    onChange={() => toggleSelectContact(contact.id)}
                                                    className="w-4 h-4 rounded"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="font-medium">{contact.email}</span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">
                                                {contact.name || '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1 flex-wrap">
                                                    {contact.tags?.map((tag) => (
                                                        <Badge key={tag} variant="secondary" className="text-xs">
                                                            {tag}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge variant="outline" className="text-xs capitalize">
                                                    {contact.source}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {new Date(contact.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setEditingContact(contact);
                                                            setEditModalOpen(true);
                                                        }}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteContact(contact.id)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add Contact Modal */}
            <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Contact</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                value={newContact.email}
                                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                                placeholder="email@example.com"
                            />
                        </div>
                        <div>
                            <Label>Name (optional)</Label>
                            <Input
                                value={newContact.name}
                                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                                placeholder="Contact name"
                            />
                        </div>
                        <div>
                            <Label>Tags</Label>
                            <div className="flex gap-2 mb-2">
                                <Input
                                    value={newTagInput}
                                    onChange={(e) => setNewTagInput(e.target.value)}
                                    placeholder="Add a tag"
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTagToForm(setNewContact, newContact.tags))}
                                />
                                <Button type="button" variant="outline" onClick={() => addTagToForm(setNewContact, newContact.tags)}>
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="flex gap-1 flex-wrap">
                                {newContact.tags.map((tag) => (
                                    <Badge key={tag} className="bg-[#C9A227]">
                                        {tag}
                                        <button onClick={() => removeTagFromForm(setNewContact, tag)} className="ml-1">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Notes (optional)</Label>
                            <Input
                                value={newContact.notes}
                                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                                placeholder="Any notes about this contact"
                            />
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button variant="outline" onClick={() => setAddModalOpen(false)} className="flex-1">
                                Cancel
                            </Button>
                            <Button onClick={handleAddContact} className="flex-1 bg-[#3D4435]">
                                Add Contact
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Import Modal */}
            <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Import Contacts</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Upload File (CSV or TXT)</Label>
                            <div className="mt-2">
                                <Input
                                    type="file"
                                    accept=".csv,.txt"
                                    onChange={handleFileUpload}
                                    className="cursor-pointer"
                                />
                            </div>
                        </div>
                        <div className="text-center text-gray-500 text-sm">or</div>
                        <div>
                            <Label>Paste Emails</Label>
                            <textarea
                                value={importData.emailText}
                                onChange={(e) => setImportData({ ...importData, emailText: e.target.value })}
                                placeholder="Paste emails (one per line, or comma/semicolon separated)"
                                className="w-full h-40 p-3 border rounded-lg resize-none"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {importData.emailText.split(/[\n,;]/).filter(e => e.trim() && e.includes('@')).length} valid emails detected
                            </p>
                        </div>
                        <div>
                            <Label>Tag (optional)</Label>
                            <Input
                                value={importData.tag}
                                onChange={(e) => setImportData({ ...importData, tag: e.target.value })}
                                placeholder="e.g., 'R&R Saturday July 2026'"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                All imported contacts will be tagged with this label
                            </p>
                        </div>
                        <div className="flex gap-2 pt-4">
                            <Button variant="outline" onClick={() => setImportModalOpen(false)} className="flex-1">
                                Cancel
                            </Button>
                            <Button onClick={handleImport} className="flex-1 bg-[#C9A227] hover:bg-[#B8922A]">
                                <Upload className="w-4 h-4 mr-2" />
                                Import
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Contact Modal */}
            <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Contact</DialogTitle>
                    </DialogHeader>
                    {editingContact && (
                        <div className="space-y-4">
                            <div>
                                <Label>Email</Label>
                                <Input value={editingContact.email} disabled className="bg-gray-100" />
                            </div>
                            <div>
                                <Label>Name</Label>
                                <Input
                                    value={editingContact.name || ''}
                                    onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                                    placeholder="Contact name"
                                />
                            </div>
                            <div>
                                <Label>Tags</Label>
                                <div className="flex gap-2 mb-2">
                                    <Input
                                        value={newTagInput}
                                        onChange={(e) => setNewTagInput(e.target.value)}
                                        placeholder="Add a tag"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                if (newTagInput.trim() && !editingContact.tags?.includes(newTagInput.trim())) {
                                                    setEditingContact({ 
                                                        ...editingContact, 
                                                        tags: [...(editingContact.tags || []), newTagInput.trim()] 
                                                    });
                                                    setNewTagInput('');
                                                }
                                            }
                                        }}
                                    />
                                    <Button 
                                        type="button" 
                                        variant="outline"
                                        onClick={() => {
                                            if (newTagInput.trim() && !editingContact.tags?.includes(newTagInput.trim())) {
                                                setEditingContact({ 
                                                    ...editingContact, 
                                                    tags: [...(editingContact.tags || []), newTagInput.trim()] 
                                                });
                                                setNewTagInput('');
                                            }
                                        }}
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                <div className="flex gap-1 flex-wrap">
                                    {editingContact.tags?.map((tag) => (
                                        <Badge key={tag} className="bg-[#C9A227]">
                                            {tag}
                                            <button 
                                                onClick={() => setEditingContact({
                                                    ...editingContact,
                                                    tags: editingContact.tags.filter(t => t !== tag)
                                                })} 
                                                className="ml-1"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label>Notes</Label>
                                <Input
                                    value={editingContact.notes || ''}
                                    onChange={(e) => setEditingContact({ ...editingContact, notes: e.target.value })}
                                    placeholder="Any notes"
                                />
                            </div>
                            <div className="flex gap-2 pt-4">
                                <Button variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1">
                                    Cancel
                                </Button>
                                <Button onClick={handleUpdateContact} className="flex-1 bg-[#3D4435]">
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ContactsPage;
